import React, { useMemo } from 'react';
import { Users, Music, ListMusic, Mic2, Calendar, PieChart as PieIcon } from 'lucide-react';

import ActivityChart from './charts/ActivityChart';
import PieChart from './charts/PieChart';
import { buildPieSlices } from './charts/chartUtils';
import { getAddedByLabel } from './spotifyUsers';

const getValidSongs = (songs) =>
  songs.filter((song) => song?.track?.name);

const countBy = (items, getKey) => {
  const counts = new Map();
  for (const item of items) {
    const key = getKey(item);
    if (!key) continue;
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  return [...counts.entries()]
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count);
};

const computeMonthlyActivity = (songs) => {
  const byMonth = new Map();
  for (const song of songs) {
    if (!song.added_at) continue;
    const month = song.added_at.slice(0, 7);
    byMonth.set(month, (byMonth.get(month) || 0) + 1);
  }
  return [...byMonth.entries()]
    .map(([month, count]) => ({ month, count }))
    .sort((a, b) => a.month.localeCompare(b.month));
};

const BarList = ({ items, maxCount, emptyMessage }) => {
  if (!items.length) {
    return <p className="analytics-empty">{emptyMessage}</p>;
  }

  return (
    <ul className="bar-list">
      {items.map((item) => (
        <li key={item.label} className="bar-list-item">
          <div className="bar-list-header">
            <span className="bar-list-label" title={item.label}>
              {item.label}
            </span>
            <span className="bar-list-count">{item.count}</span>
          </div>
          <div className="bar-track">
            <div
              className="bar-fill"
              style={{ width: `${(item.count / maxCount) * 100}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
};

const AnalyticsView = ({ songs, playlists }) => {
  const stats = useMemo(() => {
    const validSongs = getValidSongs(songs);

    const contributors = countBy(validSongs, getAddedByLabel);

    const artists = countBy(validSongs, (song) => {
      const names = song.track.artists?.map((a) => a.name).filter(Boolean);
      return names?.length ? names.join(', ') : null;
    });

    const byPlaylist = countBy(validSongs, (song) => song.playlistName);

    const monthly = computeMonthlyActivity(validSongs);
    const peakMonth = monthly.reduce(
      (best, row) => (row.count > (best?.count ?? 0) ? row : best),
      null
    );
    const avgPerMonth =
      monthly.length > 0
        ? (validSongs.length / monthly.length).toFixed(1)
        : '0';

    const uniqueArtists = new Set(
      validSongs.flatMap((song) =>
        (song.track.artists || []).map((a) => a.name).filter(Boolean)
      )
    );

    const contributorPie = buildPieSlices(contributors, 6);
    const playlistPie = buildPieSlices(byPlaylist, 6);
    const artistPie = buildPieSlices(artists, 6);

    return {
      totalSongs: validSongs.length,
      unavailableCount: songs.length - validSongs.length,
      playlistCount: playlists.length,
      contributorCount: contributors.length,
      uniqueArtistCount: uniqueArtists.size,
      contributors,
      artists: artists.slice(0, 15),
      byPlaylist,
      monthly,
      peakMonth,
      avgPerMonth,
      contributorPie,
      playlistPie,
      artistPie,
      maxContributor: contributors[0]?.count || 1,
      maxArtist: artists[0]?.count || 1,
      maxPlaylist: byPlaylist[0]?.count || 1,
    };
  }, [songs, playlists]);

  if (!songs.length) {
    return (
      <section className="analytics-section">
        <h2>Analytics</h2>
        <p className="analytics-empty">
          Fetch your collab playlists to see contributor stats, top artists, and
          activity over time.
        </p>
      </section>
    );
  }

  const summaryCards = [
    { icon: Music, label: 'Total tracks', value: stats.totalSongs },
    { icon: ListMusic, label: 'Playlists', value: stats.playlistCount },
    { icon: Users, label: 'Contributors', value: stats.contributorCount },
    { icon: Mic2, label: 'Unique artists', value: stats.uniqueArtistCount },
  ];

  return (
    <section className="analytics-section">
      <header className="analytics-header">
        <h2>Analytics</h2>
        <p className="analytics-subtitle">
          Insights across your collab playlists
          {stats.unavailableCount > 0 && (
            <span className="analytics-note">
              {' '}
              ({stats.unavailableCount} unavailable track
              {stats.unavailableCount !== 1 ? 's' : ''} excluded)
            </span>
          )}
        </p>
      </header>

      <div className="stat-cards">
        {summaryCards.map(({ icon: Icon, label, value }) => (
          <div key={label} className="stat-card">
            <Icon size={20} className="stat-card-icon" />
            <div className="stat-card-value">{value}</div>
            <div className="stat-card-label">{label}</div>
          </div>
        ))}
      </div>

      <div className="analytics-panel analytics-panel-wide activity-panel">
        <h3>
          <Calendar size={18} /> Additions over time
        </h3>
        <ActivityChart
          monthly={stats.monthly}
          peakMonth={stats.peakMonth}
          avgPerMonth={stats.avgPerMonth}
        />
      </div>

      <div className="analytics-pie-row">
        <div className="analytics-panel analytics-panel-pie">
          <h3>
            <PieIcon size={18} /> By contributor
          </h3>
          <PieChart
            slices={stats.contributorPie.slices}
            total={stats.contributorPie.total}
            emptyMessage="No contributor data"
          />
        </div>
        <div className="analytics-panel analytics-panel-pie">
          <h3>
            <PieIcon size={18} /> By playlist
          </h3>
          <PieChart
            slices={stats.playlistPie.slices}
            total={stats.playlistPie.total}
            emptyMessage="No playlist data"
          />
        </div>
        <div className="analytics-panel analytics-panel-pie">
          <h3>
            <PieIcon size={18} /> By artist
          </h3>
          <PieChart
            slices={stats.artistPie.slices}
            total={stats.artistPie.total}
            emptyMessage="No artist data"
          />
        </div>
      </div>

      <div className="analytics-grid">
        <div className="analytics-panel">
          <h3>
            <Users size={18} /> Top contributors
          </h3>
          <BarList
            items={stats.contributors.slice(0, 12)}
            maxCount={stats.maxContributor}
            emptyMessage="No contributor data"
          />
        </div>

        <div className="analytics-panel">
          <h3>
            <Mic2 size={18} /> Top artists
          </h3>
          <BarList
            items={stats.artists}
            maxCount={stats.maxArtist}
            emptyMessage="No artist data"
          />
        </div>

        <div className="analytics-panel">
          <h3>
            <ListMusic size={18} /> Songs per playlist
          </h3>
          <BarList
            items={stats.byPlaylist}
            maxCount={stats.maxPlaylist}
            emptyMessage="No playlist data"
          />
        </div>
      </div>
    </section>
  );
};

export default AnalyticsView;
